import api, { API_URL } from './client'

/** Строит URL на бэкендовый прокси для аудио трека с правильными CORS-
 *  заголовками. Используется для WaveSurfer (decode через Web Audio API
 *  требует CORS), когда Suno CDN не отдаёт `Access-Control-Allow-Origin`.
 *  Авторизация через query-param `token` т.к. `<audio crossorigin>` не
 *  умеет кастомные заголовки. */
export function getProxiedTrackAudioUrl(trackId: string): string {
	const token = api.getToken()
	const t = token ? `?token=${encodeURIComponent(token)}` : ''
	return `${API_URL}/api/v1/music/tracks/${trackId}/audio-proxy${t}`
}

export interface MusicGenerateParams {
	prompt: string
	style: string
	title: string
	model: string
	instrumental: boolean
	custom_mode: boolean
	negative_tags?: string
	vocal_gender?: string
	/** UUID записи в suno_personas — для консистентного голоса между треками. */
	persona_id?: string | null
}

export interface MusicGenerateResponse {
	ok: boolean
	request_id?: string
	task_id?: string
	credits_spent?: number
	error?: string
	/** Для wav_convert recovery: backend уже вытащил URL через record-info. */
	wav_url?: string | null
	/** Для music_cover recovery: backend уже вытащил URL'ы обложек. */
	cover_urls?: string[] | null
	/** Для describe (Mureka): синхронный анализ трека. */
	describe_data?: DescribeData | null
}

/** Анализ трека от Mureka /song/describe (жанр/настроение/инструменты/описание). */
export interface DescribeData {
	instrument: string[]
	genres: string[]
	tags: string[]
	description: string
}

/** Анализ трека (Mureka-only, синхронно): жанр/настроение/инструменты/описание. */
export async function describeTrack(track_id: string): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/describe', { method: 'POST', body: { track_id } })
}

/** Soundtrack (Mureka-only): фото/видео → музыка. Async — результат опрашивается как обычная генерация. */
export async function generateSoundtrack(params: {
	media_url: string
	is_video?: boolean
	model?: string
	prompt?: string
	title?: string
	style?: string
	/** Кусок ролика в секундах, под который писать музыку (видео). Пусто =
	 *  весь ролик. Минимум 3 секунды — правило Mureka, бэк проверяет до списания. */
	audio_start_sec?: number
	audio_end_sec?: number
}): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/soundtrack', { method: 'POST', body: params })
}

export async function generateMusicAsync(params: MusicGenerateParams): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/generate-async', {
		method: 'POST',
		body: params,
	})
}

export const MUSIC_STYLES = [
	'Pop', 'Rock', 'Electronic', 'Jazz', 'Classical',
	'Hip-Hop', 'R&B', 'Folk', 'Country', 'Latin',
	'Metal', 'Ambient', 'Lo-fi', 'Reggae', 'Blues',
	'Funk', 'Soul', 'Disco', 'Indie', 'Punk',
	'Alternative', 'K-Pop', 'Rap', 'Trap', 'House',
	'Techno', 'Synthwave', 'Cinematic', 'Drum and Bass',
]

export interface LyricsGenerateParams {
	prompt: string
	model?: string
	/** @deprecated У Suno /lyrics нет режима customMode — prompt всегда
	 *  ограничен 200 символами. Поле больше не отправляется; оставлено
	 *  опциональным только для обратной совместимости типа. */
	custom_mode?: boolean
}

export interface LyricsGenerateResponse {
	ok: boolean
	request_id?: string
	task_id?: string
	credits_spent?: number
	error?: string
	/** Mureka /lyrics синхронный — текст готов сразу (без поллинга). */
	completed?: boolean
	lyrics?: string
	title?: string
}

export async function generateLyrics(params: LyricsGenerateParams): Promise<LyricsGenerateResponse> {
	return api.request<LyricsGenerateResponse>('/api/v1/music/lyrics', {
		method: 'POST',
		body: params,
	})
}

/** Партитура трека: PDF + MusicXML (движок Mureka, работает и для чужих
 *  треков по публичной ссылке). Синхронно — файлы готовы к концу запроса. */
export async function transcribeTrack(track_id: string): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/transcribe', {
		method: 'POST',
		body: { track_id },
	})
}

export interface ScoreResponse {
	ok: boolean
	pdf_url?: string | null
	musicxml_url?: string | null
	result_urls?: string[]
	error?: string
}

/** Готовая партитура трека. 404 (ok:false) — ещё не делали. */
export async function getTrackScore(trackId: string): Promise<ScoreResponse> {
	try {
		return await api.request<ScoreResponse>(`/api/v1/music/tracks/${trackId}/score`, { method: 'GET' })
	} catch {
		return { ok: false }
	}
}

/** Подсказать СЛЕДУЮЩУЮ строку к уже написанному тексту (только Mureka).
 *  Синхронно: строка приходит в `lyrics`. Операция платная — каждая строка. */
export async function extendLyrics(
	params: { lyrics: string; model?: string },
): Promise<LyricsGenerateResponse> {
	return api.request<LyricsGenerateResponse>('/api/v1/music/lyrics/extend', {
		method: 'POST',
		body: params,
	})
}

export interface BoostStyleParams {
	content: string
	model?: string
}

export interface BoostStyleResponse {
	ok: boolean
	result?: string
	credits_spent?: number
	error?: string
}

export async function boostStyle(params: BoostStyleParams): Promise<BoostStyleResponse> {
	return api.request<BoostStyleResponse>('/api/v1/music/boost-style', {
		method: 'POST',
		body: params,
	})
}

export interface SoundsGenerateParams {
	prompt: string
	sound_loop?: boolean
	sound_tempo?: number
	sound_key?: string
	grab_lyrics?: boolean
}

export interface SoundsGenerateResponse {
	ok: boolean
	request_id?: string
	task_id?: string
	credits_spent?: number
	error?: string
}

export async function generateSounds(params: SoundsGenerateParams): Promise<SoundsGenerateResponse> {
	return api.request<SoundsGenerateResponse>('/api/v1/music/sounds', {
		method: 'POST',
		body: params,
	})
}

export interface WavConvertParams {
	track_id: string
	model?: string
}

export interface WavConvertResponse {
	ok: boolean
	request_id?: string
	task_id?: string
	credits_spent?: number
	error?: string
	/** Если Suno сказал «already exists» и бэк recover'ил URL через /wav/record-info —
	 *  возвращает готовый wav_url здесь. Фронт может кэшировать+скачать без polling. */
	wav_url?: string | null
}

export async function convertToWav(params: WavConvertParams): Promise<WavConvertResponse> {
	return api.request<WavConvertResponse>('/api/v1/music/wav-convert', {
		method: 'POST',
		body: params,
	})
}

export interface ExtendParams {
	track_id: string
	model?: string
	prompt?: string
	style?: string
	title?: string
	continue_at?: number
	negative_tags?: string
	vocal_gender?: string
	persona_id?: string | null
}

export async function extendTrack(params: ExtendParams): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/extend', { method: 'POST', body: params })
}

export interface AddInstrumentalParams {
	track_id: string
	model?: string
	/** Описание желаемого инструментала. По гайду — обязательное поле. */
	tags?: string
	/** По гайду — обязательное. Если пусто, бэк подставит "None". */
	negative_tags?: string
	title?: string
	vocal_gender?: string
	/** Mureka: какую партию догенерировать. Пусто/'Instrumental' — минус
	 *  целиком (как раньше), иначе конкретный инструмент: Drums | Bass |
	 *  Guitar | Keyboard | Percussion | Strings | Synth | FX | Brass |
	 *  Woodwinds. Suno это поле игнорирует. */
	track_type?: string
}

export async function addInstrumental(params: AddInstrumentalParams): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/add-instrumental', { method: 'POST', body: params })
}

export interface AddVocalsParams {
	track_id: string
	model?: string
	prompt?: string
	style?: string
	title?: string
	negative_tags?: string
	vocal_gender?: string
	/** UUID персоны — для Mureka голос добавленного вокала. */
	persona_id?: string | null
}

export async function addVocals(params: AddVocalsParams): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/add-vocals', { method: 'POST', body: params })
}

export async function generateCover(params: { track_id: string }): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/cover', { method: 'POST', body: params })
}

export type AudioState = 'available' | 'restorable' | 'expired'

/** Точная доступность аудио трека: бэк проверяет наш S3 (range-байт) И провайдера
 *  (Suno/Mureka). ОДИН источник истины — без расхождения «по дате ↔ по данным».
 *  available — играет; restorable — наш файл истёк, но провайдер ещё хранит оригинал
 *  (исторический статус старого бэка — фронт трактует как expired: провайдер
 *  хранит файл ~3 дня, «вернуть» после нашей 30-дневной чистки нечего);
 *  expired — срок хранения истёк. */
export async function getAudioStatus(
	trackId: string,
): Promise<{ state: AudioState; providerError?: boolean }> {
	return api.request<{ state: AudioState; providerError?: boolean }>(
		`/api/v1/music/tracks/${trackId}/audio-status`,
		{ method: 'GET' },
	)
}

export interface SetTrackCoverResponse {
	ok: boolean
	image_url?: string
	error?: string
}

/** Применить URL обложки к треку — перезаписывает suno_tracks.image_url.
 *  Используется после выбора юзером одного из двух вариантов обложки,
 *  и в будущем — для custom-обложек из upload'а. */
export async function setTrackCover(trackId: string, imageUrl: string): Promise<SetTrackCoverResponse> {
	return api.request<SetTrackCoverResponse>(`/api/v1/music/tracks/${trackId}/cover`, {
		method: 'PATCH',
		body: { image_url: imageUrl },
	})
}

/** Одна обложка из истории трека (галерея «Генератора обложек»). */
export interface TrackCover {
	id: string
	image_url: string | null
	/** webp-варианты {размер: url} для лёгких превью/лайтбокса (Фаза 2). */
	variants?: Record<string, string> | null
	prompt: string | null
	/** Реальный промпт, отданный в модель (у авто-обложек — текст арт-директора). */
	effective_prompt?: string | null
	prompt_source?: string | null // 'user' | 'art_director'
	image_model?: string | null
	params?: Record<string, unknown> | null
	width?: number | null
	height?: number | null
	source: string // 'generated' | 'uploaded'
	created_at: string | null
	is_active: boolean
}

/** Все обложки трека (история генераций), новые сверху. Активная — та, чей
 *  URL совпадает с suno_tracks.image_url. Подвязать другую — setTrackCover
 *  (бесплатно, уже оплачено). */
export async function listTrackCovers(
	trackId: string,
): Promise<{ ok: boolean; covers: TrackCover[]; active_url?: string | null; error?: string }> {
	return api.request(`/api/v1/music/tracks/${trackId}/covers`, { method: 'GET' })
}

/** Удалить обложку из галереи. Активную удалить нельзя (409) — сначала
 *  подвязать другую. Возвращает новый covers_count. */
export async function deleteTrackCover(
	trackId: string,
	coverId: string,
): Promise<{ ok: boolean; covers_count?: number; error?: string }> {
	return api.request(`/api/v1/music/tracks/${trackId}/covers/${coverId}`, { method: 'DELETE' })
}

/** Обложка с инфой о треке — для вкладки «Обложки» в /files (все обложки юзера). */
export interface MyCover extends TrackCover {
	track_id: string
	track_title: string | null
}

/** Все обложки пользователя (любой трек), новые сверху — для вкладки «Обложки»
 *  в /files. Универсально: Suno / Mureka / загруженные треки. */
export async function getMyCovers(
	params?: { limit?: number; offset?: number },
): Promise<{ ok: boolean; covers: MyCover[]; total: number; has_more: boolean }> {
	const q = new URLSearchParams()
	if (params?.limit) q.set('limit', String(params.limit))
	if (params?.offset) q.set('offset', String(params.offset))
	const qs = q.toString()
	return api.request(`/api/v1/music/covers${qs ? `?${qs}` : ''}`, { method: 'GET' })
}

/** «Загрузить свой трек» в генератор обложек: регистрирует внешний (сделанный
 *  не у нас) аудиофайл как трек. Аудио живёт 1 день, запись (имя+обложка) —
 *  навсегда. `track_id` задан → re-attach аудио к существующей обложке (без
 *  оплаты). Бесплатно. */
export async function importTrack(params: {
	upload_url: string
	file_key?: string
	title?: string
	track_id?: string
}): Promise<{ ok: boolean; track_id?: string; title?: string; error?: string }> {
	return api.request('/api/v1/music/tracks/import', { method: 'POST', body: params })
}

export async function replaceSection(params: { track_id: string; model?: string; prompt?: string; style?: string; title?: string; replace_start?: number; replace_end?: number; persona_id?: string | null }): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/replace-section', { method: 'POST', body: params })
}

export async function generatePersona(params: { track_id: string }): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/persona', { method: 'POST', body: params })
}

export async function generateMashup(params: { track_ids: string[] }): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/mashup', { method: 'POST', body: params })
}

/** Модель разделения Mureka: '-1' до 5 дорожек, '-2' до 12 + ноты,
 *  '-3' голос + минус («Минусовка»). Пусто = прежнее поведение. */
export type StemModel = 'audio-separation-1' | 'audio-separation-2' | 'audio-separation-3'

export async function stemSeparation(
	/** `stem_kind` — что нужно юзеру: 'stems' (разбор по инструментам) или
	 *  'minus' (голос + минус). Как это получить у Suno/Mureka, решает бэкенд;
	 *  separate_type/stem_model остаются для отладки и старых вызовов. */
	params: { track_id: string; stem_kind?: StemKind; separate_type?: string; stem_model?: StemModel },
): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/stem-separation', { method: 'POST', body: params })
}

export async function generateMidi(params: { track_id: string }): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/midi', { method: 'POST', body: params })
}

/** Караоке-видео Mureka: формат кадра и оформление.
 *  ВАЖНО (проверено живьём): оформление влияет ТОЛЬКО когда в видео есть
 *  обложка (use_cover) — без неё все семь шаблонов дают одинаковый вид. */
export type ClipAspect = '16:9' | '9:16' | '3:4' | '4:3'
export type ClipLayout = 'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | 'layout_6' | 'layout_7'

export async function createMusicVideo(
	params: { track_id: string; aspect_ratio?: ClipAspect; layout?: ClipLayout; use_cover?: boolean },
): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/music-video', { method: 'POST', body: params })
}

/** Клип по треку (Mureka «Music Video» поверх Seedance): короткий ролик под
 *  ВЫБРАННЫЙ отрезок трека — с липсинком персонажа и субтитрами лирики.
 *  Асинхронный: возвращает task_id, готовность тянем getMurekaClipStatus. */
export type MurekaClipRatio = '16:9' | '9:16' | '3:4' | '4:3' | '1:1' | '21:9'
export type MurekaClipResolution = '480p' | '720p' | '1080p' | '4k'

export interface MurekaClipParams {
	track_id: string
	/** Границы отрезка трека в МИЛЛИСЕКУНДАХ. */
	audio_start: number
	audio_end: number
	/** Длительность ролика в секундах (их модель делает секунды, не всю песню). */
	duration?: number
	ratio?: MurekaClipRatio
	resolution?: MurekaClipResolution
	/** Модель Seedance (см. CLIP_MODELS); пусто — дефолт провайдера (2.5). */
	model?: string
	prompt?: string
	background?: string
	show_lyrics_subtitle?: boolean
	lip_sync?: boolean
	/** URL фото персонажей. Seedance отклоняет реальные лица — ошибка придёт
	 *  от провайдера, показываем её текст как есть. */
	character?: string[]
	/** Стык сцен: ролик начнётся ровно с этого кадра… */
	first_frame_url?: string
	/** …или закончится этим кадром (image-to-video Seedance). */
	last_frame_url?: string
	/** Ролик сцены-источника — уходит модели как reference_video (поверх кадра). */
	reference_video_url?: string
	/** «Анализ кадра» — наш платный vision-слой стыка (описание кадра модели). */
	scene_vision?: boolean
	/** Эквалайзер сцены (темп/настроение/свет/съёмка, 0–4) — сохраняется с
	 *  запросом, продолжение наследует настройки источника. */
	scene_knobs?: Record<string, number> | null
}

export async function createMurekaClip(params: MurekaClipParams): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/clip', { method: 'POST', body: params })
}

export interface MurekaClipStatus {
	ok: boolean
	/** queued | running | succeeded | failed | cancelled | expired */
	status?: string
	video_url?: string
	duration?: number
	resolution?: string
	error?: string
}

/** Множители цены клипа — правятся в админке; фронт считает сумму на кнопке
 *  по ним же, по каким бэк списывает (дефолты — DEFAULT_CLIP_PRICING в caps). */
export interface ClipPricing {
	base_seconds: number
	model: Record<string, number>
	resolution: Record<string, number>
	lip_sync: number
}

export async function getClipPricing(): Promise<{ ok: boolean } & ClipPricing> {
	return api.request<{ ok: boolean } & ClipPricing>('/api/v1/music/clip-pricing')
}

export async function getMurekaClipStatus(taskId: string): Promise<MurekaClipStatus> {
	return api.request<MurekaClipStatus>(`/api/v1/music/clip-status/${encodeURIComponent(taskId)}`)
}

/** Клип трека в списке: готовый (смотреть/скачать) или идущий (возобновить
 *  поллинг). Юзер платил за каждый — ни один не должен пропасть из виду. */
export interface TrackClipItem {
	task_id: string
	status: 'processing' | 'completed' | 'failed'
	video_url: string
	created_at?: string | null
	ratio?: string
	duration?: number | null
	resolution?: string
	/** Модель Seedance сцены — продолжение наследует (HD только у 2.0). */
	model?: string
	error?: string
	/** Отрезок трека (мс), на который снят клип — карта зон на дорожке. */
	audio_start?: number | null
	audio_end?: number | null
	/** Настройки сцены клипа — «Сцена до/после» наследует их, чтобы сегменты
	 *  вышли в одном стиле и собирались в единый ролик. */
	prompt?: string
	character?: string[]
	lip_sync?: boolean
	show_lyrics_subtitle?: boolean
	/** Кадры стыка (появляются у готового клипа): «Сцена после» стартует с
	 *  last, «Сцена до» заканчивается first — image-to-video у Seedance. */
	frame_first?: string
	frame_last?: string
	/** Кадры-ИСТОЧНИКИ: от какой версии соседа снята эта сцена. По ним
	 *  считаются точные связи версий (∞ только у реально состыкованных). */
	src_frame_first?: string
	src_frame_last?: string
	/** Эквалайзер, с которым снималась сцена — наследуется продолжением. */
	scene_knobs?: Record<string, number> | null
}

export async function getTrackClips(trackId: string): Promise<{ ok: boolean; items: TrackClipItem[] }> {
	return api.request<{ ok: boolean; items: TrackClipItem[] }>(`/api/v1/music/tracks/${trackId}/clips`)
}

export async function getTimestampedLyrics(params: { track_id: string }): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/timestamped-lyrics', { method: 'POST', body: params })
}

export interface SunoTrack {
	id: string
	external_audio_id: string
	external_task_id: string
	track_index: number
	title: string | null
	tags: string | null
	duration: number | null
	audio_url: string | null
	stream_audio_url: string | null
	image_url: string | null
	/** webp-варианты активной обложки {размер: url} для лёгких превью (Фаза 2):
	 *  чип=320, превью=640, лайтбокс=1280. null = вариантов нет → фронт на image_url. */
	cover_variants?: Record<string, string> | null
	/** У трека есть готовые клипы («Клип по треку») — акцент-бейдж в списке. */
	has_clips?: boolean
	lyrics: string | null
	/** Оригинальный текст генерации ЭТОЙ версии (requests.prompt — что юзер вписал
	 *  в виджет / в операцию). Кабинет: пилюля «Текст» (копи-онли). null если пусто
	 *  (операции без текста — инструментал/продление-без-слов). */
	prompt?: string | null
	/** Сохранённый анализ трека (Mureka /song/describe). Кабинет: пилюля «Анализ»
	 *  (показ без повторного платного запроса). null если не анализировали. */
	describe?: DescribeData | null
	model: string | null
	operation: string
	source_track_id: string | null
	/** «Основной» дубль генерации — лицо карточки в ленте «Мои треки». Приходит
	 *  из /family (для бейджа «Основной» в кабинете). undefined вне family-данных. */
	is_primary?: boolean
	created_at: string | null
	/** Загруженный (imported) трек живёт 1 день — ISO-время удаления аудио.
	 *  null = не imported или аудио уже удалено. operation==='imported' + null
	 *  audio_url = «трек удалён — подвяжи заново». */
	deletes_at?: string | null
	/** URL lossless WAV-версии. null если пока не конвертировали. Заполняется
	 *  после успешного wav_convert — дальше повторное скачивание WAV бесплатно. */
	wav_url?: string | null
	/** UUID альбомов (suno_albums) в которых состоит трек. Нужно фронту для
	 *  предзаполнения чекбоксов в AddToAlbumSheet. Пустой массив = не в альбомах. */
	album_ids?: string[]
	/** UUID персоны (suno_personas) созданной из этого трека как исходника.
	 *  null если из трека персон не создавалось. Фронт показывает иконку 🎙️
	 *  на треках-источниках. */
	persona_id?: string | null
	/** Имя персоны (для тултипа иконки). Может быть null если юзер не
	 *  переименовал — бэк тогда возвращает null, UI показывает дефолтный текст. */
	persona_name?: string | null
	/** URL сгенерированного music-video. null если видео не делали.
	 *  Фронт: 🎬 бейдж + клик → открыть плеер. Источник — requests.result_url. */
	video_url?: string | null
	/** Клип КОГДА-ЛИБО генерился, даже если video_url уже вычищен по сроку хранения →
	 *  кабинет рисует мьютнутую иконку «срок истёк» (честная история). Из /family. */
	has_video?: boolean
	/** Есть ли готовый MIDI (task_events.response_data.raw.midi_data).
	 *  Фронт: MIDI бейдж + клик → GET /tracks/:id/midi → viewer. */
	has_midi?: boolean
	/** Есть ли готовый timestamped_lyrics (aligned_words для караоке).
	 *  Фронт: LRC бейдж + клик → GET /tracks/:id/timestamped → karaoke viewer. */
	has_timestamped?: boolean
	/** Lossless-файл (Mureka отдаёт его вместе с генерацией). У Suno пусто. */
	flac_url?: string | null
	/** Есть ли готовый ПОЛНЫЙ разбор — дорожки по инструментам. */
	has_stems?: boolean
	/** Есть ли готовая минусовка (вокал + минус). Отдельный флаг: это другой
	 *  результат за другие деньги, и кнопки не должны обещать чужой вид. */
	has_minus?: boolean
	/** Есть ли готовая партитура (PDF + MusicXML). */
	has_score?: boolean
	/** Сколько обложек накоплено у трека (история генераций). ≥2 → за чипом
	 *  «папка» + клик открывает галерею. Активная одна (image_url). */
	covers_count?: number
	/** Сколько караоке-клипов сделано у версии (по одному на оформление) —
	 *  стикер-счётчик на пилюле, когда их больше одного. */
	videos_count?: number
	/** Сколько версий сделано из этой генерации (прямые дети её дублей) — для
	 *  бейджа «N версий» в ленте: среди одинаковых имён сразу видно, где наработки.
	 *  Приходит только из ленты (exclude_children); undefined в light/кабинете. */
	version_count?: number
	/** Была неудачная MIDI-попытка для этого трека (Suno не смог построить ноты).
	 *  Заранее это не узнать → кабинет прячет кнопку «Ноты» после первого фейла.
	 *  Из /family. */
	midi_failed?: boolean
	/** Момент удаления аудио-файла чисткой (30-дневный S3-кэш). Если задан —
	 *  трек 🔴 «удалён», аудио не вернуть. ISO-строка. Из /family (и позже /tracks). */
	audio_deleted_at?: string | null
	/** Когда аудио будет удалено (created_at + 30 дн) — для UI «доступно до …» на
	 *  живых треках. ISO-строка. Из /family. */
	audio_expires_at?: string | null
	/** Скрыт ли трек из инструмента «Обложки для музыки» (не из библиотеки) —
	 *  юзер убрал из раздела; музыка и обложка при этом целы. */
	cover_tool_hidden?: boolean
	// Параметры генерации (из requests.params, для отображения в CardModal)
	vocal_gender?: string
	gen_style?: string
	gen_instrumental?: boolean
	gen_custom_mode?: boolean
}

export interface TracksResponse {
	ok: boolean
	tracks: SunoTrack[]
	total: number
}

export async function getUserTracks(params?: {
	limit?: number
	offset?: number
	operation?: string
	/** Серверный фильтр по нескольким операциям (CSV на бэке): фильтры-пилюли
	 *  библиотеки мапятся на списки (extend → extend,upload_extend). */
	operations?: string[]
	/** Исключить операции (для «Все»: lyrics/music_cover — не аудио). */
	excludeOperations?: string[]
	/** Фильтр по нейросети-провайдеру: 'suno' | 'mureka'. Пусто — все. */
	provider?: string
	/** Light-режим: только базовые поля (без album/persona/midi/video/…) —
	 *  на порядок быстрее для пикеров. Heavy-поля придут дефолтами. */
	light?: boolean
	/** Скрыть треки-версии (source_track_id IS NOT NULL) — для ленты «Мои
	 *  треки»: версии показываются в кабинете родителя, не в общем списке. */
	excludeChildren?: boolean
}): Promise<TracksResponse> {
	const query = new URLSearchParams()
	if (params?.limit) query.set('limit', String(params.limit))
	if (params?.offset) query.set('offset', String(params.offset))
	const ops = params?.operations?.length ? params.operations.join(',') : params?.operation
	if (ops) query.set('operation', ops)
	if (params?.excludeOperations?.length)
		query.set('exclude_operation', params.excludeOperations.join(','))
	if (params?.provider) query.set('provider', params.provider)
	if (params?.light) query.set('light', 'true')
	if (params?.excludeChildren) query.set('exclude_children', 'true')
	const qs = query.toString()
	return api.request<TracksResponse>(`/api/v1/music/tracks${qs ? '?' + qs : ''}`, { method: 'GET' })
}

export interface TrackVersionsResponse {
	ok: boolean
	versions: SunoTrack[]
	total: number
}

/** Версии трека для «Кабинета трека»: открытый трек + его аудио-производные
 *  (серверная группировка по source_track_id, без лимита). Эндпоинт может
 *  отсутствовать на старом бэке → кабинет ловит ошибку и группирует из /tracks. */
export async function getTrackVersions(trackId: string): Promise<TrackVersionsResponse> {
	return api.request<TrackVersionsResponse>(`/api/v1/music/tracks/${trackId}/versions`, { method: 'GET' })
}

export interface TrackVariant {
	id: string
	title: string | null
	tags: string | null
	duration: number | null
	audio_url: string | null
	stream_audio_url: string | null
	image_url: string | null
	model: string | null
	operation: string
	track_index: number
	created_at: string | null
}
export interface TrackVariantsResponse {
	ok: boolean
	variants: TrackVariant[]
	total: number
}
/** «Дубли» — варианты одной генерации (общий request_id, track_index 0/1). НЕ
 *  версии; эндпоинт может отсутствовать на старом бэке → caller ловит ошибку. */
export async function getTrackVariants(trackId: string): Promise<TrackVariantsResponse> {
	return api.request<TrackVariantsResponse>(`/api/v1/music/tracks/${trackId}/variants`, { method: 'GET' })
}

/** «Семья генерации» — корень + дубли (source_track_id NULL) + ВСЕ их версии
 *  (рекурсивно). Плоский список; дерево строится на фронте по source_track_id.
 *  Эндпоинт может отсутствовать на старом бэке → caller ловит ошибку. */
export async function getTrackFamily(trackId: string): Promise<TracksResponse> {
	return api.request<TracksResponse>(`/api/v1/music/tracks/${trackId}/family`, { method: 'GET' })
}

/** «Нота» дубля для карточки ленты: сам дубль + число переработок (рекурсивно
 *  вглубь) + есть ли видео в его ветке. */
export interface TrackNote {
	id: string
	track_index: number
	version_count: number
	/** node_id узла-носителя доп-выхода в ветке дубля (или отсутствует). Значок на
	 *  ноте показывается, если узел есть; клик по значку открывает кабинет на ЭТОМ
	 *  узле → дерево разворачивается до него. song = ноты/караоке (объединены).
	 *  Из notes-summary; на старом бэке отсутствуют. */
	video_node?: string | null
	cover_node?: string | null
	stems_node?: string | null
	song_node?: string | null
	describe_node?: string | null
	/** Жив ли клип (result_url не вычищен по сроку). false → лента мьютит видео-иконку
	 *  со статусом «срок истёк». undefined (старый бэк) → не мьютим. */
	video_alive?: boolean
}

/** Батч «нот» для ленты: на каждый track_id (представитель карточки) — список его
 *  дублей. Эндпоинт может отсутствовать на старом бэке → caller ловит ошибку. */
export async function getNotesSummary(trackIds: string[]): Promise<{ ok: boolean; summary: Record<string, TrackNote[]> }> {
	return api.request(`/api/v1/music/tracks/notes-summary`, {
		method: 'POST',
		body: { track_ids: trackIds },
	})
}

/** Делает дубль «основным» — он становится лицом генерации в ленте «Мои треки».
 *  Принимает любой узел семьи (бэк резолвит корневой дубль и снимает флаг с
 *  siblings). Возвращает id выбранного дубля. Эндпоинт может отсутствовать на
 *  старом бэке → caller ловит ошибку (выбор не сохранится, но UI не падает). */
export async function setPrimaryTrack(trackId: string): Promise<{ ok: boolean; primary_track_id?: string }> {
	return api.request<{ ok: boolean; primary_track_id?: string }>(`/api/v1/music/tracks/${trackId}/primary`, { method: 'POST' })
}

/** «Восстановить с оригинала» — аудио в нашем S3 кэшируется 30 дней, потом
 *  удаляется. Бэк перезапрашивает свежий URL у Suno/Mureka по external_task_id
 *  и обновляет audio_url. Возвращает новую ссылку (или ошибку, если оригинал у
 *  провайдера тоже истёк). */

export interface TrackCountsResponse {
	ok: boolean
	/** Сырые счётчики по операциям (generate/extend/…) — маппинг в категории
	 *  фильтров остаётся на фронте (audioFilters.ts). */
	counts: Record<string, number>
	/** Счётчики по нейросети: { suno, mureka, other }. Для фильтра-провайдера.
	 *  Может отсутствовать на старом бэке — фронт деградирует мягко. */
	provider_counts?: Record<string, number>
}

/** Счётчики треков для фильтров-пилюль библиотеки. С пагинацией /tracks фронт
 *  не видит весь список — счётчики отдаёт бэк одним GROUP BY. `null` — бэк
 *  ещё без эндпоинта (graceful fallback на клиентский подсчёт).
 *  `provider` (suno/mureka) скоупит операционные counts; provider_counts всегда
 *  глобальные. */
export async function getTrackCounts(provider?: string, excludeChildren?: boolean): Promise<TrackCountsResponse | null> {
	try {
		const q = new URLSearchParams()
		if (provider) q.set('provider', provider)
		if (excludeChildren) q.set('exclude_children', 'true')
		const qs = q.toString()
		return await api.request<TrackCountsResponse>(`/api/v1/music/tracks/counts${qs ? '?' + qs : ''}`, { method: 'GET' })
	} catch {
		return null
	}
}

/** Удалить трек из библиотеки пользователя. Бэкенд каскадом чистит
 *  `suno_album_tracks`, обнуляет `suno_personas.source_track_id` и
 *  `suno_tracks.source_track_id`. Исторические requests/events остаются. */
export async function deleteTrack(trackId: string): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/music/tracks/${trackId}`, { method: 'DELETE' })
}

/** Скрыть/вернуть трек в инструменте «Обложки для музыки» — не трогает музыку и
 *  обложку, только видимость в ленте инструмента (cover_tool_hidden). Для
 *  библиотечных треков, которые юзер убирает из раздела. */
export async function setCoverToolHidden(
	trackId: string,
	hidden: boolean,
): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/music/tracks/${trackId}/cover-tool-visibility`, {
		method: 'PATCH',
		body: { hidden },
	})
}

// ─── Lazy-fetch данных результатов по клику на бейдж ────────────────────
// Флаги has_midi/has_timestamped/video_url приходят сразу в GET /tracks
// (дешёвые booleans + URL). Сами данные (midi JSON, aligned_words) большие,
// поэтому фетчатся отдельно только когда юзер реально открывает viewer.

/** Файл нот (.mid) — так ноты приходят у Mureka: архив, а не JSON. */
export interface MidiFile {
	name: string
	url: string
}

export interface MidiDataResponse {
	ok: boolean
	midi_data?: { instruments?: unknown[]; [key: string]: unknown }
	/** Готовые .mid-файлы (Mureka). У Suno пусто — там JSON выше. */
	files?: MidiFile[]
	error?: string
}

export async function getTrackMidi(trackId: string): Promise<MidiDataResponse> {
	return api.request<MidiDataResponse>(`/api/v1/music/tracks/${trackId}/midi`, { method: 'GET' })
}

export interface TrackVideoResponse {
	ok: boolean
	video_url?: string | null
	result_urls?: string[]
	completed_at?: string | null
	/** Оформление и формат ПОСЛЕДНЕГО клипа. Пусто у старых. */
	layout?: string
	aspect_ratio?: string
	/** ВСЕ клипы трека (по одному на сделанное оформление), свежие первыми —
	 *  юзер платит за каждый стиль, ни один не должен пропадать. */
	items?: { video_url?: string | null; layout?: string; aspect_ratio?: string; completed_at?: string | null }[]
	error?: string
}

export async function getTrackVideo(trackId: string): Promise<TrackVideoResponse> {
	return api.request<TrackVideoResponse>(`/api/v1/music/tracks/${trackId}/video`, { method: 'GET' })
}

export interface TimestampedWord {
	word: string
	startS: number
	endS?: number
	success?: boolean
	p_align?: number
}

export interface TimestampedResponse {
	ok: boolean
	aligned_words?: TimestampedWord[]
	waveform_data?: unknown
	hoot_cer?: unknown
	is_streamed?: boolean
	error?: string
}

export async function getTrackTimestamped(trackId: string): Promise<TimestampedResponse> {
	return api.request<TimestampedResponse>(`/api/v1/music/tracks/${trackId}/timestamped`, { method: 'GET' })
}

/** Вид разбора: полный (дорожки по инструментам) или минусовка (голос + минус). */
export type StemKind = 'stems' | 'minus'

export interface StemsGroup {
	kind: StemKind
	result_urls: string[]
	labels: string[]
	completed_at?: string | null
}

export interface StemsResponse {
	ok: boolean
	/** URL'ы дорожек (вокал/бас/драмы/инструментал…) — самый свежий набор. */
	result_urls?: string[]
	/** Метки дорожек из имён файлов (parallel к result_urls). */
	labels?: string[]
	completed_at?: string | null
	/** Все виды разбора, что делались для трека (свежий результат каждого).
	 *  Минусовка и полный разбор стоят отдельно — показываем оба, чтобы
	 *  второй запуск не прятал оплаченный первый. */
	groups?: StemsGroup[]
	error?: string
}

/** Дорожки стем-разделения трека — группой. 404 пока стемы не готовы. */
export async function getTrackStems(trackId: string): Promise<StemsResponse> {
	try {
		return await api.request<StemsResponse>(`/api/v1/music/tracks/${trackId}/stems`, { method: 'GET' })
	} catch {
		return { ok: false }
	}
}

export async function uploadCover(params: {
	track_id?: string
	upload_url?: string
	model?: string
	custom_mode?: boolean
	instrumental?: boolean
	prompt?: string
	style?: string
	title?: string
	negative_tags?: string
	vocal_gender?: string
}): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/upload-cover', { method: 'POST', body: params })
}

export async function uploadExtend(params: {
	track_id?: string
	upload_url?: string
	model?: string
	instrumental?: boolean
	prompt?: string
	style?: string
	title?: string
	continue_at?: number
	negative_tags?: string
	vocal_gender?: string
}): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/upload-extend', { method: 'POST', body: params })
}

/** «Референс»: загруженный аудиофайл (≥30 сек) как стилевой референс → Mureka
 *  генерит НОВУЮ песню в его стиле. Только Mureka. `upload_url` — URL уже
 *  загруженного в наше хранилище аудио (см. uploadFile). */
export async function referenceGenerate(params: {
	upload_url: string
	model?: string
	prompt?: string
	style?: string
	title?: string
	instrumental?: boolean
	custom_mode?: boolean
	vocal_gender?: string
	persona_id?: string
}): Promise<MusicGenerateResponse> {
	return api.request<MusicGenerateResponse>('/api/v1/music/reference', { method: 'POST', body: params })
}

// ═══════════════════════════════════════════════════════════════════
// Обложки треков — генерация через gpt-image-2 (инструмент)
// ═══════════════════════════════════════════════════════════════════

export interface CoverArtResponse {
	ok: boolean
	/** Готовый (подписанный) URL обложки — уже применён к треку на бэке. */
	image_url?: string
	/** webp-варианты {размер: url} новой обложки — чтобы превью/чип обновились
	 *  мгновенно (без перезагрузки /tracks). */
	variants?: Record<string, string> | null
	width?: number
	height?: number
	credits_spent?: number
	/** Сколько обложек у трека после этой генерации (для бейджа-папки). */
	covers_count?: number
	error?: string
}

/** Сгенерировать обложку для трека через gpt-image-2 и применить её.
 *  Синхронно: бэк возвращает готовый image_url (уже записан в трек). */
export async function generateCoverArt(params: {
	track_id: string
	/** Свой промпт; если пусто — бэк соберёт из стиля/названия трека. */
	prompt?: string
	/** Фото-референс (URL) → режим «улучшить фото» (edit), иначе генерация с нуля. */
	image_url?: string
	quality?: 'low' | 'medium' | 'high'
}): Promise<CoverArtResponse> {
	return api.request<CoverArtResponse>('/api/v1/music/cover-art', { method: 'POST', body: params })
}

/** Актуальная цена генерации обложки (кредиты). null — бэк ещё без эндпоинта
 *  (graceful: инструмент покажет нейтральный лейбл вместо числа). */
export async function getCoverArtPrice(): Promise<number | null> {
	try {
		// skipAuth: публичная витринная цена (гостевой /bots/tools) — без него
		// 401 гостя утаскивал в hard-redirect /login, и страница была невидима
		// краулерам (поймано prerender-валидацией).
		const res = await api.request<{ ok: boolean; credits: number }>(
			'/api/v1/music/cover-art/price',
			{ method: 'GET', skipAuth: true },
		)
		return res.ok ? res.credits : null
	} catch {
		return null
	}
}

// ═══════════════════════════════════════════════════════════════════
// Suno Personas — голосовые профили
// ═══════════════════════════════════════════════════════════════════

export interface SunoPersona {
	id: string
	external_persona_id: string
	name: string | null
	image_url: string | null
	source_track_id: string | null
	/** 'track' (клон из трека) | 'upload' (свой голос из записи/файла). */
	source?: string
	/** 'suno' | 'mureka' — для фильтра совместимости в picker'е. */
	provider?: string
	description?: string | null
	created_at: string | null
}

export interface PersonasListResponse {
	ok: boolean
	personas: SunoPersona[]
	total: number
}

export async function getUserPersonas(params?: {
	limit?: number
	offset?: number
}): Promise<PersonasListResponse> {
	const query = new URLSearchParams()
	if (params?.limit) query.set('limit', String(params.limit))
	if (params?.offset) query.set('offset', String(params.offset))
	const qs = query.toString()
	// Graceful fallback: если backend ещё не задеплоил personas endpoint (404),
	// возвращаем пустой список вместо throw — чтобы UI не ломался на юзере
	// пока идёт деплой. Реальные ошибки (5xx / network) всё ещё пробрасываются.
	try {
		return await api.request<PersonasListResponse>(
			`/api/v1/music/personas${qs ? '?' + qs : ''}`,
			{ method: 'GET' },
		)
	} catch (err) {
		const msg = err instanceof Error ? err.message.toLowerCase() : ''
		if (msg.includes('404') || msg.includes('not found') || msg.includes('request failed')) {
			return { ok: true, personas: [], total: 0 }
		}
		throw err
	}
}

export async function renamePersona(personaId: string, name: string): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/music/personas/${personaId}`, {
		method: 'PATCH',
		body: { name },
	})
}

export async function deletePersona(personaId: string): Promise<{ ok: boolean; error?: string }> {
	return api.request(`/api/v1/music/personas/${personaId}`, {
		method: 'DELETE',
	})
}

/** «Свой голос»: клон из загруженного/записанного аудио (Mureka vocal-clone).
 *  upload_url — URL уже загруженного в наше хранилище аудио (см. uploadFile).
 *  Синхронно возвращает созданную персону (без поллинга). */
/** Цена создания голоса (Mureka persona). Возвращает число кредитов. */
export async function getVoicePrice(): Promise<number> {
	const r = await api.request<{ ok: boolean; credits: number }>('/api/v1/music/voices/price', { method: 'GET' })
	return r.credits
}

export async function createVoice(params: {
	upload_url: string
	name: string
	description?: string
	content_type?: string
}): Promise<{ ok: boolean; persona?: SunoPersona & { credits_spent?: number }; error?: string }> {
	return api.request('/api/v1/music/voices', { method: 'POST', body: params })
}

/** Отдельный инструмент «Извлечь вокал»: отделяет вокал от музыки/шума
 *  (Mureka stem + очистка) → URL чистой дорожки. Потом из неё клонируют голос. */
export async function isolateVocals(params: {
	upload_url: string
	content_type?: string
}): Promise<{ ok: boolean; url?: string; error?: string }> {
	return api.request('/api/v1/music/voices/isolate-vocals', { method: 'POST', body: params })
}

// ═══════════════════════════════════════════════════════════════════
// Аватар голоса (картинка персоны) — загрузка (бесплатно) / ИИ (платно)
// ═══════════════════════════════════════════════════════════════════

/** Применить загруженную картинку как аватар голоса (бесплатно): пишет
 *  suno_personas.image_url. image_url — URL уже загруженного фото (uploadFile). */
export async function setVoiceAvatar(
	personaId: string,
	imageUrl: string,
): Promise<{ ok: boolean; image_url?: string; error?: string }> {
	return api.request(`/api/v1/music/personas/${personaId}`, {
		method: 'PATCH',
		body: { image_url: imageUrl },
	})
}

/** Сгенерировать аватар голоса через gpt-image-2 (платно). Если передан
 *  `image_url` (загруженное фото) — режим «улучшить фото» (edit с референсом);
 *  иначе — генерация с нуля по промпту. Синхронно: бэк возвращает image_url. */
export async function generateVoiceAvatar(
	personaId: string,
	params: { prompt?: string; image_url?: string; quality?: 'low' | 'medium' | 'high' },
): Promise<{ ok: boolean; image_url?: string; credits_spent?: number; error?: string }> {
	return api.request(`/api/v1/music/personas/${personaId}/avatar`, {
		method: 'POST',
		body: params,
	})
}
